import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import Sidebar from "../components/Sidebar";
import SecondaryButton from "../components/SecondaryButton";
import PrimaryButton from "../components/PrimaryButton";
import { useDispatch, useSelector } from "react-redux";
import { convertToLocalTime } from "../utils/convertTime";
import {
  acceptOutletService,
  deleteOutletService,
} from "../features/outletSlice";
import toast from "react-hot-toast";
import { MobileResponsive } from "./MobileResponsive";
import { getEntityData } from "../utils/mapUtils";
import useDataFetch from "../hooks/useDataFetch";
import { Map } from "@vis.gl/react-google-maps";
import { MapMarker } from "../components/MapMarker";
import { InfoWindowPopup } from "../components/InfoWindow";
import FloatingInput from "../components/FloatingInput";
import { otpService } from "../features/authSlice";
import DynamicMetaTags from "../components/DynamicMetaTags";
import Avatar from "react-avatar";

const ManageOutletDetail = () => {
  const { id } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { data, isLoading } = useDataFetch(`/admin/outlet-detail/${id}`);

  const { data: user } = useDataFetch(`/admin/profile`);

  const [errors, setErrors] = useState({
    otp: "",
  });

  const [sidebars, setSidebars] = useState({
    deleteOutlet: false,
  });

  const [deleteOutlet, setDeleteOutlet] = useState({
    deleteOutlet: false,
  });
  const [otp, setOtp] = useState(false);

  const sidebarRefs = {
    deleteOutlet: useRef(null),
  };

  /* Google Maps */
  const [selectedMarker, setSelectedMarker] = useState({
    selectedMarker: null,
    entityData: [],
  });
  const [infoWindowShown, setInfoWindowShown] = useState(false);

  const handleMarkerClick = (id, marker, entityType) => {
    setSelectedMarker(() => ({
      selectedMarker: marker,
      entityData: getEntityData(id, entityType, data?.outlet),
    }));
    setInfoWindowShown(true);
  };

  const handleCloseInfoWindow = () => {
    setInfoWindowShown(false);
    setSelectedMarker({
      selectedMarker: null,
      entityData: null,
    });
  };

  const onMapClick = useCallback(() => {
    setSelectedMarker({
      selectedMarker: null,
      entityData: [],
    });
    setInfoWindowShown(false);
  }, []);

  useEffect(() => {
    if (
      selectedMarker.entityData?.length > 0 &&
      selectedMarker.selectedMarker
    ) {
      setInfoWindowShown(true);
    }
  }, [selectedMarker]);

  const handleAcceptRequest = async () => {
    const resultAction = await dispatch(acceptOutletService({ outletID: id }));
    if (acceptOutletService.fulfilled.match(resultAction)) {
      toast.success(resultAction?.payload?.message);
      navigate("/manage-outlets");
    }
  };

  const handleRejectRequest = async (e) => {
    e.preventDefault();
    const resultAction = await dispatch(
      deleteOutletService({ outletID: id, otp: otp })
    );

    if (deleteOutletService.fulfilled.match(resultAction)) {
      toast.success(resultAction?.payload?.message);
      navigate("/manage-outlets");
    }
  };

  const handleSendOtp = async () => {
    if (!isLoading) {
      const resultAction = await dispatch(otpService({ email: user?.email }));

      if (otpService.fulfilled.match(resultAction)) {
        toast.success(resultAction?.payload?.message);

        setDeleteOutlet((prevState) => ({
          ...prevState,
          deleteOutlet: true,
        }));
      }
    }
  };

  const handleOtpBlur = useCallback(() => {
    if (!otp || otp.length !== 5) {
      setErrors((prevErrors) => ({
        ...prevErrors,
        otp: "OTP should be 5 digits long",
      }));
    } else {
      setErrors((prevErrors) => ({ ...prevErrors, otp: "" }));
    }
  }, [otp]);

  const toggleSidebar = (key, value) => {
    setSidebars((prev) => ({ ...prev, [key]: value }));
  };

  const handleCloseSidebar = () => {
    setSidebars({
      orderPricing: false,
      suspendOutlet: false,
      deleteOutlet: false,
      driversPoint: false,
      cancelTrip: false,
    });
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        !Object.keys(sidebarRefs).some((key) =>
          sidebarRefs[key]?.current?.contains(event.target)
        )
      ) {
        handleCloseSidebar();
      }
    };

    if (Object.values(sidebars).some((val) => val)) {
      document.addEventListener("mousedown", handleClickOutside);
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [sidebars]);

  if (isLoading) {
    return (
      <>
        <MobileResponsive />
        <div className="lg:flex hidden items-start">
          <Sidebar />
          <div className="ml-[14.5vw] w-full h-screen flex items-center justify-center bg-gray-50">
            <div className="flex flex-col items-center gap-2">
              <div className="h-12 w-12 rounded-full border-4 border-t-quaternary border-b-quaternary border-r-transparent border-l-transparent animate-spin"></div>
              <p className="text-gray-600 font-medium">Loading..</p>
            </div>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <DynamicMetaTags
        pageTitle="Beep - Review and Onboard New Outlets"
        pageDescription="Take control of your outlets with Beep! Effortlessly manage new outlet requests, view detailed outlet information, track payment history, and set pickup prices. Enable or disable outlets with a click!"
        pageKeywords="Beep, outlet management, new outlet requests, outlet details, payment history, set pickup prices, enable outlets, disable outlets, active outlets"
      />
      <MobileResponsive />
      <div className="lg:flex hidden items-start">
        <Sidebar />

        {Object.values(sidebars).some((isOpen) => isOpen) && (
          <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm z-30 transition-opacity duration-500 ease-in-out"></div>
        )}

        <div className="ml-[14.5vw] w-full h-screen overflow-y-auto px-10 py-8 text-secondary flex items-start relative">
          <div className="w-full flex items-start">
            <div className="w-[50%]">
              <div className="flex w-full">
                <Link to="/manage-outlets" className="flex items-center gap-3">
                  <div className="bg-[#F75E00] w-5 h-5 flex items-center justify-center rounded-full pr-[1.5px]">
                    <img
                      src="/assets/left-white-arrow.svg"
                      alt=""
                      className="w-[6px]"
                    />
                  </div>
                  <span className="font-semibold text-base">Back</span>
                </Link>
              </div>

              {isLoading ? (
                <p className="py-8">Loading..</p>
              ) : (
                <div className="py-8 w-full">
                  <div className="border-2 border-quaternary 2xl:w-[71%] xl:w-[84.5%] lg:w-[84.5%] px-5 py-1.5 rounded-lg flex items-center gap-3 bg-[#F7F9F8]">
                    {/* <img
                      src="/assets/user.png"
                      alt=""
                      className="h-[5.3rem] w-[5.3rem] rounded-full"
                    /> */}
                    <Avatar
                      name={data?.outlet?.restaurant_name}
                      size="84"
                      round={true}
                      maxInitials={2}
                      textSizeRatio={2.5}
                      style={{ letterSpacing: "1px" }}
                    />
                    <div className="text-center">
                      <h1 className="text-[21px] font-bold tracking-wide capitalize">
                        {data?.outlet?.restaurant_name}
                      </h1>
                    </div>
                  </div>

                  <h2 className="text-[17px] font-semibold pt-6 pb-2">
                    Outlet Details
                  </h2>

                  <div className="border-2 border-quaternary 2xl:w-[71%] xl:w-[84.5%] lg:w-[84.5%] px-5 py-1.5 rounded-lg flex flex-col items-center justify-between bg-[#F7F9F8]">
                    {[
                      {
                        label: "Contact number",
                        value: data?.outlet?.phone_no || "—",
                      },
                      {
                        label: "Email",
                        value: data?.outlet?.email || "—",
                      },
                      {
                        label: "Zipcode",
                        value: data?.outlet?.zip_code || "—",
                      },
                      {
                        label: "Onboarding date",
                        value:
                          convertToLocalTime(data?.outlet?.createdAt) || "—",
                      },
                    ].map((item, idx) => (
                      <div key={idx} className="flex items-center w-full py-2">
                        <p className="2xl:text-[15px] xl:text-[13px] lg:text-[13px] font-semibold tracking-wide w-1/2 break-all">
                          {item.label}
                        </p>
                        <p className="2xl:text-[15px] xl:text-[13px] lg:text-[13px] font-semibold tracking-wide w-1/2 break-all">
                          {item.value}
                        </p>
                      </div>
                    ))}
                  </div>

                  <h2 className="text-[17px] font-semibold pt-6 pb-2">
                    Outlet Address
                  </h2>

                  <div className="border-2 border-quaternary 2xl:w-[71%] xl:w-[84.5%] lg:w-[84.5%] px-5 py-5 rounded-lg flex flex-col items-center justify-between bg-[#F7F9F8]">
                    {data?.outlet?.address}
                  </div>

                  <div className="2xl:w-[71%] xl:w-[84.5%] lg:w-[84.5%] flex items-center gap-3 py-5">
                    <PrimaryButton
                      text="Accept outlet"
                      uppercase={false}
                      mutation={true}
                      width="w-full"
                      event={handleAcceptRequest}
                    />
                    <SecondaryButton
                      text="Reject"
                      uppercase={false}
                      mutation={true}
                      width="w-full"
                      event={() => toggleSidebar("deleteOutlet", true)}
                    />
                  </div>
                </div>
              )}
            </div>

            <div className="py-12 w-[50%]">
              <div className="border-2 border-quaternary 2xl:w-[80%] xl:w-[87%] lg:w-[88%] h-[70vh] rounded-lg flex flex-col items-center justify-center gap-4 bg-[#F7F9F8] mt-2">
                {isLoading ? (
                  <div className="w-full h-full flex flex-col items-center justify-center gap-4">
                    <div className="w-full h-full bg-gray-200 rounded-lg animate-pulse"></div>
                  </div>
                ) : (
                  <Map
                    mapId={"terrain"}
                    defaultZoom={8}
                    defaultCenter={{
                      lat: 28,
                      lng: 77,
                    }}
                    gestureHandling={"greedy"}
                    onClick={onMapClick}
                    disableDefaultUI
                    styles={"terrain"}
                    fullscreenControl={true}
                    mapTypeId={"terrain"}
                    mapTypeControlOptions
                    zoomControl
                    scaleControl
                    mapTypeControl
                    rotateControl
                  >
                    <MapMarker
                      data={[data?.outlet]}
                      entityType="outlets"
                      onMouseEnter={handleMarkerClick}
                    />

                    <InfoWindowPopup
                      infoWindowShown={infoWindowShown}
                      selectedMarker={selectedMarker}
                      handleCloseInfoWindow={handleCloseInfoWindow}
                    />
                  </Map>
                )}
              </div>
            </div>
          </div>

          {/* Delete Outlet Sidebar */}
          <div
            ref={sidebarRefs.deleteOutlet}
            className={`fixed right-0 top-0 h-full w-[40%] bg-white shadow-lg overflow-y-auto transform transition-transform duration-[500ms] ease-in-out z-40 border-t-4 border-primary ${
              sidebars.deleteOutlet ? "translate-x-0" : "translate-x-full"
            }`}
          >
            <div className="p-8 relative flex flex-col items-center">
              <button
                onClick={handleCloseSidebar}
                className="absolute top-4 right-4 text-gray-500 hover:text-gray-700 scale-150"
              >
                &times;
              </button>
              <div className="flex w-[90%] pb-10">
                <div className="flex items-center gap-3 cursor-pointer">
                  <div className="bg-[#F75E00] w-6 h-6 flex items-center justify-center rounded-full">
                    <img
                      src="/assets/left-white-arrow.svg"
                      alt=""
                      className="w-[7px]"
                    />
                  </div>
                  <span className="font-semibold text-[25px]">
                    Delete outlet
                  </span>
                </div>
              </div>

              <div className="border-2 border-quaternary w-[90%] mb-5 px-5 py-1.5 rounded-lg flex items-center gap-3 bg-[#F7F9F8]">
                <img
                  src="/assets/user.png"
                  alt={data?.outlet?.restaurant_name}
                  className="h-[5.3rem] w-h-[5.3rem] rounded-full"
                />
                <Avatar
                  name={data?.outlet?.restaurant_name}
                  size="84"
                  round={true}
                  maxInitials={2}
                  textSizeRatio={2.5}
                  style={{ letterSpacing: "1px" }}
                />
                <div className="text-center">
                  <h1 className="text-xl font-bold tracking-wide capitalize">
                    {data?.outlet?.restaurant_name}
                  </h1>
                </div>
              </div>

              <div className="w-[90%] mb-5 px-5 py-3 rounded-lg flex justify-between items-center shadow-lg">
                <p className="2xl:text-[18px] xl:text-[18px] lg:text-[16px] font-semibold w-full">
                  {data?.outlet?.phone_no}
                </p>
                <div className="w-full flex justify-end items-center">
                  <PrimaryButton
                    text={"Call outlet"}
                    path={`tel:${data?.outlet?.phone_no}`}
                    width="w-[60%]"
                    uppercase={false}
                  />
                </div>
              </div>

              <div className="w-[90%] px-[4px] py-5 rounded-lg">
                <div className="2xl:text-lg xl:text-lg lg:text-[16px]">
                  {!deleteOutlet.deleteOutlet ? (
                    <>
                      <p>
                        This account is scheduled for permanent deletion. If you
                        wish to proceed with deleting the account{" "}
                        <strong className="capitalize">
                          {data?.outlet?.restaurant_name}
                        </strong>
                        , please request a one-time password (OTP) for
                        confirmation.
                      </p>

                      <div className="w-[90%] m-auto flex flex-col justify-center gap-5 py-10">
                        <PrimaryButton
                          text="Request a OTP"
                          width="w-full"
                          uppercase={false}
                          event={handleSendOtp}
                        />
                      </div>
                    </>
                  ) : (
                    <>
                      <form
                        action=""
                        className="w-full"
                        onSubmit={(e) => e.preventDefault()}
                      >
                        <div className="py-5 flex flex-col gap-5">
                          <label
                            htmlFor="OTP"
                            className="2xl:text-[17px] xl:text-[15px] lg:text-[14px] font-semibold text-[#413F3F]"
                          >
                            Please enter the OTP sent to your email address
                          </label>
                          <FloatingInput
                            name="otp"
                            id="otp"
                            type="number"
                            placeholder="Enter OTP"
                            value={otp}
                            error={errors.otp}
                            handleBlur={handleOtpBlur}
                            onChange={(e) => setOtp(e.target.value)}
                          />
                        </div>

                        <div className="w-full m-auto flex flex-col justify-center gap-5 pt-5">
                          <PrimaryButton
                            text="Confirm"
                            width="w-full"
                            uppercase={false}
                            event={handleRejectRequest}
                          />
                        </div>
                      </form>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default ManageOutletDetail;
